<template>
  <div class="player" :class="openState ? 'opened' : 'collapsed'">
    <div class="player-header d-flex align-items-center justify-content-between" @click="toggleExpand">
      <div class="flex-grow-1 header-title d-flex align-items-center">
        <div class="play-pause d-inline-block">
          <img v-show="showPlay" @click.prevent="play()" class="mix-play-icon" src="/images/play_circle.svg" />
          <img v-show="showPause" @click.prevent="pause()" class="mix-play-icon" src="/images/pause_circle.svg" />
          <img v-show="showSpinner" src="/images/spinner.svg" class="spinner" />
          <img
            v-show="state != 'begin-ab'"
            @click.prevent="startAB"
            class="mix-play-icon ab-text"
            :class="disableAB"
            :src="canAB ? '/images/ab.svg' : '/images/ab_disabled.svg'"
          />
        </div>
        <span class="mix-title-header handle">
          <span class="mix-title">
            <router-link v-if="policy.show_song" :to="`/songs/${mix.song_id}`">{{ mixName }}</router-link>
            <span v-else>{{ mixName }}</span>
          </span>
          {{ mixVersion }}
          <span class="mix-title-description">
            {{ mix.description }}
          </span>
        </span>
        <span class="mix-title-mobile">
          {{ mixName }}
          {{ mixVersion }}
        </span>
      </div>
      <div class="d-none d-xl-block header-date">
        {{ mixCreatedAt }}
      </div>
      <div class="header-right-controls text-right d-flex">
        <div v-if="incompleteNoteCount > 0" class="incomplete-note-count d-none d-sm-inline-block">
          <div>
            {{ incompleteNoteCount }}
          </div>
        </div>
        <a v-if="showPlaylistDelete" href="#" @click.prevent="$emit('remove-from-playlist')">
          <img src="/images/minus_circle.svg" alt="remove from playlist" title="remove from playlist"
        /></a>
        <a v-else-if="!playlistID" href="#" @click.prevent="addToPlaylist"
          ><img src="/images/plus_circle.svg" alt="add to playlist" title="add to playlist"
        /></a>
        <a href="#" v-if="policy.share" @click.prevent="shareMix"
          ><img class="share-icon" src="/images/share-plane.svg" alt="share mix" title="share mix"
        /></a>
        <img v-if="playlistID" src="/images/drag_handle.svg" class="handle" />
        <a href="#" @click.prevent="toggleExpand()" class="expand-link">
          <img v-if="openState" class="expand-icon" src="/images/expand_more.svg" alt="collapse" title="collapse" />
          <img v-else class="expand-icon" src="/images/expand_less.svg" alt="expand" title="expand" />
        </a>
      </div>
    </div>
    <div class="player-body">
      <div class="player-body-wrapper">
        <div class="mix-description">{{ mix.description }}</div>
        <div class="d-flex w-100 mix-row">
          <mix-volume
            :mixID="mix.id"
            :initialVolume="mix.metadata.volume"
            :initialOffset="initialOffset"
            @input="onInput"
          />
          <div class="waveform flex-grow-1" ref="waveform"></div>
        </div>
        <mix-markers
          v-if="openState"
          :mixID="mix.id"
          :duration="duration"
          :canEditMarkers="mix.policy.update_markers"
          @marker-click="play($event.time)"
        />
        <div class="seek-time">{{ formattedTime }}</div>
        <div class="d-flex flex-row">
          <div class="note-highlight-text" :class="{ on: noteHighlightText != ''}">{{ noteHighlightText }}</div>
          <div class="flex-grow-1 header-right-controls additional-controls">
            <span class="mix-filename d-none d-sm-inline">{{ mix.filename }}</span>
            <a href="#" v-if="policy.destroy" @click="deleteMix" class="delete-mix">
              <img class="delete-icon" src="/images/trash.svg" alt="delete" title="delete" />
            </a>
            <router-link v-if="policy.edit" :to="`/mixes/${mix.id}/edit`">
              <img class="edit-icon" src="/images/edit.svg" alt="edit" title="edit" />
            </router-link>
            <a :href="audioSrc" class="download-link">
              <img class="download-icon" src="/images/download.svg" alt="download mix" title="download" />
            </a>
          </div>
        </div>
        <mix-player-notes
          ref="mix-player-notes"
          class="mix-player-notes"
          :mix="mix"
          :current-time="currentTime"
          :isOpen="openState"
          :regions="regions"
          @play-at-time="play"
          @update-incomplete-note-count="incompleteNoteCount = $event"
          @update-note-highlight-text="noteHighlightText = $event"
        />
      </div>
    </div>
  </div>
</template>
<style scoped>
.player {
  transition-property: background-color;
  transition-duration: 0.2s;
  width: 100%;
  padding-top: 0px;
  padding-bottom: 0px;
  background-clip: padding-box;
}

.player.collapsed {
  border-bottom: 1px solid #ddd;
}
.player.playing {
  background-color: #fff;
}

.player-header {
  color: #111;
}

.player.opened .player-header {
  border-bottom: none;
}

.player-header.collapsed {
  padding-top: 5px;
  padding-bottom: 5px;
}

.player-header > div {
  padding: 5px 0px 5px 5px;
}

@media (min-width: 576px) {
  .player-header > div {
    margin-left: 5px;
    margin-right: 5px;
  }
}

.player-body {
  overflow-x: visible;
  overflow-y: hidden;
}

.player.collapsed .player-body {
  height: 0px;
}

.player-body-wrapper {
  border-bottom: 1px solid #ddd;
  padding-bottom: 20px;
  padding: 10px;
  padding-top: 2px;
}

.player:last-child .player-body-wrapper {
  border-bottom: none;
}

.header-date {
  padding-right: 5px;
  font-size: 12px;
  vertical-align: bottom;
  color: #333;
  text-align: left;
}

.header-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mix-title,
.mix-title a {
  color: #222;
  font-family: arial;
  font-weight: 600;
  font-size: 1.1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mix-title-mobile {
  display: none;
}

.mix-title a {
  text-decoration: underline;
  text-decoration-color: #aaa;
}

.mix-title-description {
  font-size: 75%;
  margin-left: 30px;
  color: #aaa;
}

.player.opened .mix-title-description {
  display: none;
}

.mix-description {
  color: #666;
  font-style: italic;
}

.header-right-controls {
  text-align: right;
  line-height: 18px;
}

.header-right-controls svg,
.header-right-controls img {
  max-width: 18px;
  max-height: 18px;
  color: #666;
  margin-left: 3px;
  margin-right: 3px;
}

.mix-action-buttons {
  font-size: 16px;
}

.mix-action-buttons a {
  color: #ff8181;
}

.mix-row {
  position: relative;
}

.seek-time {
  text-align: right;
  line-height: 10px;
  font-size: 10px;
  color: #111;
  margin-top: -10px;
  padding-bottom: 10px;
  padding-left: 12px;
}

.mix-filename {
  font-size: 75%;
  color: #999999;
  margin-right: 20px;
}

.spinner {
  width: 22px;
  height: 22px;
  fill: black;
  margin-right: 5px;
}

.ab-text {
  margin-left: 2px;
  margin-right: 5px;
}

.waveform {
  margin-left: 20px;
  margin-right: 30px;
  margin-top: 5px;
  padding-left: 0px;
  padding-right: 0px;
  height: 100px;
}

.expand-icon {
  width: 15px;
  height: 22px;
}

.note-highlight-text {
  font-size: 14px;
  line-height: 14px;
  transition: opacity 0.25s ease-out;
  opacity: 0;
}

.note-highlight-text.on {
  opacity: 1;
}

@media (max-width: 576px) {
  .player {
    padding: 8px;
  }

  .ab-text {
    display: none;
  }

  .play-pause {
    margin-right: 10px;
  }

  .play-pause img {
    width: 1.8rem;
    height: 1.8rem;
  }

  .expand-link {
    display: none;
  }

  .header-right-controls {
    line-height: 24px;
  }

  .waveform {
    margin-left: 0px;
    margin-right: 0px;
  }

  .additional-controls {
    display: none;
  }

  .header-right-controls svg,
  .header-right-controls img {
    max-width: 24px;
    max-height: 24px;
    margin-left: 10px;
  }

  .header-title {
    max-width: 75%;
  }
  .mix-title-header {
    display: none;
  }
  .mix-title-mobile {
    display: block;
    font-size: 145%;
    color: #222;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>

<script>
import MixVolume from "./mix_volume.vue"
import MixPlayerNotes from "./mix_player_notes.vue"
import MixMarkers from "./mix_markers.vue"
import timeFormat from "../utils/time_format"

import WaveSurfer from "wavesurfer.js"
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.js"
import bus from "../bus"

export default {
  components: { MixVolume, MixPlayerNotes, MixMarkers },
  props: ["mix", "isOpen", "canAB", "playlistID", "canEditPlaylist"],
  data() {
    return {
      noteHighlightText: "",
      incompleteNoteCount: this.mix.incomplete_note_count,
      currentTime: 0,
      regions: undefined,
      explicitState: undefined,
      state: "pause",
      volume: 0,
      initialOffset: 0,
      initializing: false,
      initializedAudio: false,
      ws: null,
      duration: undefined,
      reinitTimer: null,
      audioContext: null,
    }
  },
  methods: {
    toggleExpand(ev) {
      if (ev) {
        const interactive = event.target.closest("a, img, svg")

        if (interactive) return
      }
      if (this.explicitState === undefined) {
        this.explicitState = this.isOpen
      }
      this.explicitState = !this.explicitState
    },
    getCsrfToken() {
      const metaTag = document.querySelector('meta[name="csrf-token"]')
      return metaTag ? metaTag.getAttribute("content") : ""
    },
    updateState(state) {
      if (this.state == "begin-ab") {
        // ignore pause/play events when entering ab
        return
      }
      this.state = state
      this.$emit("update-state", state)
    },
    updateTime(event) {
      this.currentTime = event
      this.$emit("update-time", { time: event, duration: this.duration })
    },
    startAB() {
      if (!this.canAB) return
      this.updateState("begin-ab")
    },
    endAB() {
      this.state = "play"
      this.$emit("update-state", "play")
    },
    async deleteMix() {
      if (window.confirm("Delete this mix permanently?")) {
        const csrfToken = this.getCsrfToken()
        fetch("/mixes/" + this.mix.id, {
          headers: {
            accept: "application/json",
            "X-CSRF-Token": csrfToken,
          },
          method: "DELETE",
        }).then(response => {
          if (response.status == 200) {
            window.location.reload()
          } else {
            response.text().then(errBody => alert("Trouble deleting the mix: " + errBody))
          }
        })
      }
    },
    back15() {
      let cur = this.getCurrentTime()
      this.seekTo(Math.max(0, cur - 15))
    },
    forward15() {
      this.seekTo(this.getCurrentTime() + 15)
    },
    fetchMetadata() {
      if (this.metadata) return this.metadata
      return fetch("/mixes/" + this.mix.id + "/metadata").then(response => {
        if (!response.ok) {
          throw new Error("HTTP error " + response.status)
        }
        this.metadata = response.json()
        return this.metadata
      })
    },
    isPlaying() {
      if ( !this.ws || !this.initializedAudio )
        return false
      return this.ws.isPlaying()
    },
    async play(time) {
      this.updateState("begin-playback")
      await this.initWS()
      this.initWSAudio()
      this.ws.play(time)
    },
    getCurrentTime() {
      if (!this.ws) return 0
      return this.ws.getCurrentTime()
    },
    setCurrentTime(time) {
      if (!this.ws) return
      return this.ws.setTime(time)
    },
    pause() {
      if (this.ws) {
        this.ws.pause()
        this.startReinitTimer()
      }
    },
    startReinitTimer() {
      /* chrome (& maybe safari IDK) caches our 302 redirects to the s3 link
        inside the <media> element.  re-initializing the audio elements forces
        chrome to re-request the link the next time we play the audio, preventing
        us from trying to hit expired linkskies */
      this.reinitTimer = setTimeout(() => {
        if (this.isPlaying()) return
        this.initializedAudio = false
        this.initWSAudio()
      }, 10000)
    },
    async seekTo(time) {
      await this.initWS()
      this.initWSAudio()
      this.ws.setTime(time)
    },
    async playPause() {
      if (this.isPlaying()) this.pause()
      else this.play()
    },
    async initWS() {
      if (this.ws || this.initializing) return
      this.initializing = true

      const md = await this.fetchMetadata()
      this.initialOffset = md.volume_offset
      this.duration = md.duration

      const regions = new RegionsPlugin()
      regions.on("region-clicked", (region, e) => {
        e.stopPropagation()
        region.play(true)
      })

      let options = {
        container: this.$refs.waveform,
        waveColor: "#ddd",
        progressColor: "#fe7c31",
        barWidth: 2,
        barMinHeight: 1,
        height: 100,
        plugins: [regions],
        duration: this.mix.duration,
      }
      if ( md.waveform ) {
        options.peaks = [md.waveform]
      }

      this.ws = WaveSurfer.create(options)
      this.ws.on("play", () => this.updateState("play"))
      this.ws.on("pause", () => this.updateState("pause"))
      this.ws.on("finish", () => {
        this.updateState("pause")
        this.seekTo(0)
        this.startReinitTimer()
      })

      this.lastTime = null
      this.ws.on("audioprocess", time => {
        time = Math.round(time)
        if (time == this.lastTime) return
        this.lastTime = time
        this.updateState("play")
        this.updateTime(time)
      })
      this.ws.on("seeking", time => this.updateTime(time))
      this.ws.on("decode", () => this.regions = regions)

      if ( !md.waveform ) {
        this.ws.load(this.audioSrc)
      }
    },
    initWSAudio() {
      if (this.initializedAudio) return

      this.initializedAudio = true

      // if the user is seeking around in the waveform before they play something
      const initTime = this.ws.getCurrentTime()

      // this has to be done separate from the main wavesurfer init
      // because ios-safari (our favorite browser) needs to create the audio
      // triggered from a click-event-chain.

      const audio = new Audio()
      audio.preload = "none"
      audio.src = this.audioSrc
      audio.crossOrigin = "use-credentials"

      if (this.audioContext) this.audioContext.close()
      this.audioContext = new AudioContext()
      const gainNode = this.audioContext.createGain()
      const newMediaNode = this.audioContext.createMediaElementSource(audio)
      newMediaNode.connect(gainNode)
      gainNode.connect(this.audioContext.destination)
      this.ws.setMediaElement(audio)

      this.ws.setVolume = function (value) {
        gainNode.gain.setValueAtTime(value, 0)
      }
      this.ws.getVolume = function () {
        return gainNode.gain.value
      }
      this.ws.setVolume(this.volume)

      this.ws.setTime(initTime)
    },
    onInput(volume) {
      if (this.ws) this.ws.setVolume(volume)
      this.volume = volume
    },
    // a-b code needs to cross fade without actually storing the volume
    setVolume(volume) {
      if (this.ws) {
        this.ws.setVolume(volume)
      }
    },
    getVolume() {
      if (this.ws) return this.ws.getVolume()
    },
    shareMix() {
      bus.emit("open-share", {
        shareTitle: this.mix.name_and_version.join(" "),
        createShareURL: "/share/mix/" + this.mix.id + ".json",
        shareObjectType: "mix",
        shareChoice: false,
      })
    },
    addToPlaylist() {
      this.$emit("add-to-playlist")
    },
  },
  computed: {
    openState() {
      if (this.explicitState !== undefined) return this.explicitState

      return this.isOpen
    },
    disableAB() {
      return { disabled: !this.canAB }
    },
    showPlay() {
      return this.state == "pause"
    },
    showPause() {
      return this.state == "play" || this.state == "begin-ab"
    },
    showSpinner() {
      return this.state == "begin-playback" || this.state == "begin-ab"
    },
    showPlaylistDelete() {
      return this.playlistID && this.canEditPlaylist
    },
    mixName() {
      return this.mix.name_and_version[0]
    },
    mixVersion() {
      return this.mix.name_and_version[1]
    },
    mixCreatedAt() {
      return new Date(this.mix.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    },
    formattedTime() {
      return timeFormat(this.currentTime, 0)
    },
    policy() {
      return this.mix.policy
    },
    audioSrc() {
      return this.mix.audio_src
    },
  },
  watch: {
    openState(val) {
      if (val && !this.initializing) {
        this.initWS()
      }
    },
  },
  beforeUnmount() {
    clearTimeout(this.reinitTimer)
    if (this.audioContext) this.audioContext.close()
  },
  async mounted() {
    if (this.isOpen) {
      await this.initWS()
    }

    bus.on("seek-to", time => {
      if ( this.state == "play" ) {
        this.seekTo(time)
      }
    })
  },
}
</script>
